import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { profileService } from '../api/profile.service';
import { PROFILE_KEYS } from './queryKeys';
import { useUIStore } from '@/core/stores/ui.store';
import { useToast } from '@/shared/hooks/useToast';
import type { UpdatePreferencesInput } from '../schemas';
import type { DateFormat } from '@/core/stores/ui.store';
import i18n from '@/core/i18n/config';

/**
 * Mutation para actualizar las preferencias del usuario autenticado.
 *
 * @remarks
 * En onSuccess:
 * 1. Invalida la query de perfil para refetch desde servidor.
 * 2. Sincroniza los stores locales (idioma, timezone, formatos).
 * 3. Muestra toast de éxito.
 */
export function useUpdatePreferences() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('settings');

  return useMutation({
    mutationFn: (data: UpdatePreferencesInput) => profileService.updatePreferences(data),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: PROFILE_KEYS.me() });

      if (variables.language) {
        i18n.changeLanguage(variables.language);
      }
      if (variables.timezone !== undefined && variables.timezone !== null) {
        useUIStore.getState().setTimezone(variables.timezone);
      }
      if (variables.dateFormat) {
        useUIStore.getState().setDateFormat(variables.dateFormat as DateFormat);
      }
      if (variables.timeFormat) {
        useUIStore.getState().setTimeFormat(variables.timeFormat);
      }

      toast.success({ title: t('preferences.saveSuccess') });
    },
  });
}
